/**
 * Dependencies
 */
import { RESTService } from '@prz-dp/services';

import app from '@/constants/application.constant';

import {
  CampaignForPartnershipModel,
  CampaignTypeEnum
} from './campaign-for-partnership.model';

/**
 * Service `CampaignForPartnershipService`.
 */
class CampaignForPartnershipService extends RESTService<
CampaignForPartnershipModel,
number
> {
  /**
   * Constructor.
   */
  constructor() {
    super(CampaignForPartnershipModel);
  }

  /**
   * Get partner campaign types.
   */
  async getPartnerCampaignTypes({
    couponId,
    partnershipId
  }: {
    couponId: number;
    partnershipId: number;
  }) {
    const response = await this.POST(
      app.onDemandCampaignsUrl,
      {
        operationName: 'campaignsForPartnership',
        query: `
          query campaignsForPartnership(
            $couponId: BigInteger!
            $partnershipId: BigInteger!
          ) {
            campaignsForPartnership(
              couponId: $couponId
              partnershipId: $partnershipId
            ) {
              campaignType
            }
          }
        `,
        variables: { couponId, partnershipId }
      },
      { headers: { 'X-Partnership-Id': partnershipId } }
    );
    const { data: result, errors } = response || {};
    const { campaignsForPartnership } = result || {};

    if (errors && errors.length) {
      throw errors;
    }

    return campaignsForPartnership;
  }

  /**
   * Get partner campaign coupons.
   */
  async getPartnerCampaignCoupons({
    partnershipId
  }: {
    partnershipId: number;
  }) {
    const response = await this.POST(
      app.onDemandCampaignsUrl,
      {
        operationName: 'partnership',
        query: `
          query partnership(
            $id: BigInteger!
          ) {
              partnership(
                id: $id
              ) {
                coupons {
                  id
                }
              }        
            } 
        `,
        variables: { id: partnershipId }
      },
      { headers: { 'X-Partnership-Id': partnershipId } }
    );
    const { data: result, errors } = response || {};
    const { partnership } = result || {};

    if (errors && errors.length) {
      throw errors;
    }

    return partnership;
  }

  /**
   * Get campaign type by id.
   */
  async getCampaignType({
    id,
    partnershipId
  }: {
    id: number;
    partnershipId: number;
  }): Promise<CampaignTypeEnum | null> {
    const response = await this.POST(
      app.onDemandCampaignsUrl,
      {
        operationName: 'campaign',
        query: `
          query campaign($id: BigInteger) {
            campaign(id: $id) {
              id
              type
            }
          }
        `,
        variables: { id }
      },
      { headers: { 'X-Partnership-Id': partnershipId } }
    );
    const { data: result, errors } = response || {};
    const { campaign } = result || {};

    if (errors && errors.length) {
      throw errors;
    }

    return campaign?.type || null;
  }
}

/**
 * Export `campaignForPartnershipService`.
 */
export const campaignForPartnershipService =
  new CampaignForPartnershipService();
